import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ArrowSquareOut, CalendarBlank, Sparkle } from "@phosphor-icons/react";

const POLL_INTERVAL_MS = 1500;
const MAX_WAIT_MS = 420_000;

export default function CaGrantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [grant, setGrant] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/grants/ca/${id}`)
      .then(({ data }) => setGrant(data))
      .catch((e) => setLoadErr(e.response?.data?.detail || "Could not load this grant"));
  }, [id]);

  const generate = async () => {
    if (!topic.trim()) { setErr("Please describe your project topic first."); return; }
    setErr("");
    setGenerating(true);
    try {
      const { data: accepted } = await api.post("/proposals/generate", {
        grant_id: `ca-${id}`,
        grant_number: String(grant.number),
        grant_title: grant.title,
        grant_agency: `${grant.agency} (State of California)`,
        grant_description: [grant.purpose, grant.description].filter(Boolean).join("\n\n").slice(0, 4000),
        topic,
      });
      const startedAt = Date.now();
      let pollErrors = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        if (Date.now() - startedAt > MAX_WAIT_MS) throw new Error("Generation is taking longer than expected. Please try again.");
        let job;
        try {
          job = (await api.get(`/proposals/jobs/${accepted.job_id}`)).data;
          pollErrors = 0;
        } catch (pe) {
          pollErrors += 1;
          if (pollErrors >= 10) throw pe;
          continue;
        }
        if (job.status === "complete" && job.proposal) { navigate(`/proposals/${job.proposal.id}`); return; }
        if (job.status === "failed") throw new Error(job.error_detail || "Generation failed");
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  if (loadErr) return <div className="p-8 text-sm text-red-700" data-testid="ca-grant-error">{loadErr}</div>;
  if (!grant) return <div className="p-8 text-sm font-mono text-zinc-500 animate-pulse">Loading grant…</div>;

  const facts = [
    ["Applicant type", grant.applicant_type],
    ["Geography", grant.geography],
    ["Est. available funds", grant.est_available_funds],
    ["Est. awards", grant.est_awards],
    ["Award amounts", grant.est_amounts],
    ["Matching funds", grant.matching_funds],
    ["Award period", grant.award_period],
    ["Expected award date", grant.exp_award_date],
    ["Categories", grant.categories],
    ["Funding source", grant.funding_source],
  ].filter(([, v]) => v);

  return (
    <div className="max-w-5xl" data-testid="ca-grant-detail">
      <Link to="/search" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900">
        <ArrowLeft size={13} /> Back to search
      </Link>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <span className="bg-amber-500 text-zinc-900 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5">California state grant</span>
        <span className="font-mono text-xs text-zinc-500">{grant.number}</span>
        {grant.status && <span className="border border-zinc-300 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 text-zinc-600">{grant.status}</span>}
      </div>
      <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-2">{grant.title}</h1>
      <div className="text-sm text-zinc-600 mt-1">{grant.agency}</div>
      <div className="flex gap-4 mt-3 text-xs font-mono text-zinc-500 flex-wrap">
        {grant.openDate && <span><CalendarBlank size={12} className="inline mr-1" /> Opens {grant.openDate}</span>}
        {grant.closeDate && <span><CalendarBlank size={12} className="inline mr-1" /> Deadline {grant.closeDate}</span>}
        {grant.grant_url && (
          <a href={grant.grant_url} target="_blank" rel="noreferrer" data-testid="ca-grant-external-link" className="text-blue-700 hover:underline inline-flex items-center gap-1">
            Official grant page <ArrowSquareOut size={12} />
          </a>
        )}
      </div>

      <div className="mt-6 grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="border border-zinc-200 bg-white p-6">
          {grant.purpose && (
            <>
              <div className="gf-overline">Purpose</div>
              <p className="text-sm text-zinc-700 mt-2 whitespace-pre-line">{grant.purpose}</p>
            </>
          )}
          {grant.description && (
            <>
              <div className="gf-overline mt-6">Description</div>
              <p className="text-sm text-zinc-700 mt-2 whitespace-pre-line">{grant.description}</p>
            </>
          )}
          {grant.contact_info && (
            <>
              <div className="gf-overline mt-6">Contact</div>
              <p className="text-xs font-mono text-zinc-600 mt-2">{grant.contact_info}</p>
            </>
          )}
        </div>

        <div className="space-y-6">
          <div className="border border-zinc-200 bg-white divide-y divide-zinc-100">
            {facts.map(([k, v]) => (
              <div key={k} className="px-4 py-2.5">
                <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{k}</div>
                <div className="text-xs text-zinc-800 mt-0.5">{String(v).slice(0, 160)}</div>
              </div>
            ))}
          </div>

          <div className="border-2 border-zinc-900 bg-white p-5">
            <div className="flex items-center gap-2">
              <Sparkle size={14} weight="fill" className="text-amber-500" />
              <span className="gf-overline">Draft a proposal with AI</span>
            </div>
            <Textarea
              data-testid="ca-topic-input"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Describe your project in a sentence or two…"
              className="gf-sharp border-zinc-300 mt-3 min-h-[80px]"
            />
            {err && <div className="text-xs text-red-700 mt-2" data-testid="ca-generate-error">{err}</div>}
            <Button
              onClick={generate}
              disabled={generating}
              data-testid="ca-generate-btn"
              className="gf-sharp w-full mt-3 bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-widest"
            >
              {generating ? "Drafting… (1–3 min)" : "Generate draft"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
